// [목적] React 앱 mount — ThemeProvider + QueryClientProvider + AuthProvider + ToastProvider + RouterProvider + (dev) MSW
// [이유] fe-screens-skeleton ADR-2/7 + fe-be-wire-up Wave 1 WC-FE-AUTH-07 + Wave 3 FEED-07 ToastProvider 추가.
//         AuthProvider 가 QueryClientProvider 하위, RouterProvider 상위에 위치 (ADR-T4 staleTime=∞ 캐시).
//         ToastProvider 는 AuthProvider 하위, RouterProvider 상위 — 라우트 컴포넌트가 useToast() 사용 가능.
// [사이드 이팩트] dev 모드이고 VITE_USE_MSW=true 일 때만 MSW worker 활성 (Wave 0 §2-D 결정).
//                 기본값 false — 실 BE 호출 우선. 기존 mock 동선 유지는 VITE_USE_MSW=true 로 복원.
// [수정 시 고려사항] AuthProvider 는 반드시 QueryClientProvider 하위에 위치해야 한다.
//                   ToastProvider 는 Snackbar DOM을 전역 렌더링하므로 RouterProvider 상위에 위치 필수.
//                   RouterProvider 는 ToastProvider 하위 — 라우트 컴포넌트가 useToast() 사용 가능.
//                   [G1-5, 2026-09-17] PwaUpdateNotifier 추가 — production 빌드에서만 내부적으로
//                   navigator.serviceWorker.register('/sw.js') 수행(컴포넌트 내부 가드, DEV/MSW 미충돌).

import { CssBaseline, ThemeProvider } from '@mui/material';
import { createMuiTheme } from '@gangwoncanvas/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';

import { AuthProvider } from './providers/AuthProvider.js';
import { ToastProvider } from './components/feedback/Toast.js';
import { RootErrorBoundary } from './components/feedback/RootErrorBoundary.js';
import { PwaUpdateNotifier } from './components/pwa/PwaUpdateNotifier.js';
import { router } from './routes.js';

const theme = createMuiTheme('light');
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false } },
});

/**
 * [목적] MSW worker 조건부 시작 — dev + VITE_USE_MSW=true 시만 활성
 * [이유] Wave 0 §2-D 결정 — VITE_USE_MSW 기본값 false (실 BE 우선). true 시 기존 mock 동선 유지
 * [사이드 이팩트] VITE_USE_MSW=false (기본) 시 MSW 코드가 번들에서 tree-shake 됨
 * [수정 시 고려사항] .env.example 의 VITE_USE_MSW 참조
 */
async function startMockWorker(): Promise<void> {
  if (!import.meta.env.DEV) return;
  if (import.meta.env.VITE_USE_MSW !== 'true') return;
  try {
    const { worker } = await import('./mocks/browser.js');
    await worker.start({ onUnhandledRequest: 'bypass' });
  } catch (err) {
    // [이유] mockServiceWorker.js 누락 등으로 worker.start 가 reject 되더라도 앱 렌더는 진행해야 함
    console.warn('[MSW] worker start failed — falling back to network passthrough', err);
  }
}

function mount(): void {
  const root = createRoot(document.getElementById('root')!);
  root.render(
    <StrictMode>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          {/* RootErrorBoundary: 라우트/프로바이더 렌더 throw 시 흰 화면 대신 에러 화면 노출(최후 그물) */}
          <RootErrorBoundary>
            {/* AuthProvider: QueryClientProvider 하위, RouterProvider 상위 (ADR-T4) */}
            <AuthProvider>
              {/* ToastProvider: AuthProvider 하위, RouterProvider 상위 — useToast() 전역 사용 가능 */}
              <ToastProvider>
                <RouterProvider router={router} />
                {/* G1-5: production 빌드에서만 SW 등록 + 업데이트 배너 — DEV/MSW 와 충돌 없음(내부 가드) */}
                <PwaUpdateNotifier />
              </ToastProvider>
            </AuthProvider>
          </RootErrorBoundary>
        </QueryClientProvider>
      </ThemeProvider>
    </StrictMode>,
  );
}

void startMockWorker().then(mount);
